import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import React from 'react';

import ButtonGoUp from '@components/common/ButtonGoUp/ButtonGoUp';
import Button from '@components/uikit/Button/Button';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { ITableSorting } from '@model/tableSorting';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import type { IArchivedAiStore } from '../../model/archivedAi';
import { useExportArchiveActionItemsMutation } from '../../services/useExportArchiveActionItemsMutation';
import { selectArchiveFiltersData } from '../../store/useArchiveFiltersStore';
import style from './style.module.scss';

interface IProps {
  isLoading: boolean;
  displayedArchivedAiCount: number;
  totalCount: number | undefined;
  archiveAiTableContainerRef: React.LegacyRef<HTMLDivElement>;
  sorting: ITableSorting;
}

const ArchiveFooter: React.FC<IProps> = ({
  isLoading,
  displayedArchivedAiCount,
  totalCount,
  archiveAiTableContainerRef,
  sorting,
}) => {
  const { t } = useTranslation();

  const { data: profileData } = useUserProfileQuery();
  const currentDateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const { mutate: exportArchiveActionItemsMutate } = useExportArchiveActionItemsMutation();

  const locationSelectValue = selectAppLocationSelectValue();
  const filterParams: IArchivedAiStore = selectArchiveFiltersData();

  const downloadArchivedAi = () => {
    showToast({ variant: 'success', message: t('The download will begin shortly.') });

    exportArchiveActionItemsMutate(
      {
        ...filterParams,
        sorting,
        locationIds:
          locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
      },
      {
        onSuccess: (data) => {
          saveAs(
            new Blob([data?.data], { type: 'text/plain;charset=utf-8' }),
            `${profileData?.customer?.name || ''} Archived Action Items Report ${format(
              new Date(),
              currentDateFormat,
            )}.xlsx`,
          );
        },
      },
    );
  };

  return (
    <div className={style.archivedAiFooter}>
      <span>
        {`${t('Displaying')} ${isLoading ? 0 : displayedArchivedAiCount} ${t('of')} ${
          isLoading ? 0 : totalCount
        } ${t('Archived Action Items')}`}
      </span>
      <div className={style.footerButtons}>
        <Button type="primary" variant={'medium'} text={t('Download report')} onClick={downloadArchivedAi} />
        <ButtonGoUp ref={archiveAiTableContainerRef} />
      </div>
    </div>
  );
};

export default ArchiveFooter;
